<template>
    <div v-dark class="quiz-result">
        <Portal to="quizResultHeader">
            <QuizResultHeader
                class="quiz-result__header"
                :quiz="currentQuiz"
                :can-retake-quiz="!!canRetakeQuiz"
                :level-up-quiz-level="levelUpQuizLevel"
                :num-of-levels-in-subject="numOfLevelsInSubject"
                :show-how-you-heard-about-us-cta="showHowYouHeardAboutUsCTA"
                :is-loading="isLoading"
                :show-header-actions="canShowQuizResultsHeaderActions"
                @retake="retakeQuiz"
                @close="closeResults"
                @goToLevelUp="goToLevelUp"
                @goToHeardAboutUsAsk="goToHeardAboutUsAsk"
            />
        </Portal>
        <QuizResultBlob
            v-if="!isLoading"
            class="quiz-result__blob"
            :color="scoreColor || 'green'"
        />
        <TutorEngagementSurvey
            v-if="!isLoading && showTutorEngagementSurvey"
            @close="showTutorEngagementSurvey = false"
            @submit="submitTutorEngagementSurvey"
        />
        <ConceptQuizSurvey
            v-if="!isLoading && showConceptQuizSurvey && conceptQuizConcept"
            :concept="conceptQuizConcept"
            @close="showConceptQuizSurvey = false"
        />
        <div
            v-dark
            class="quiz-result__banner"
            :class="{
                'quiz-result__banner--show-level-up-progress-message': showLevelUpProgressMessage,
            }"
        >
            <img
                v-if="!isDarkMode && displayConfetti"
                src="@/assets/celebrations/lightModeConfetti.gif"
                class="quiz-result__celebration-gif"
                alt=""
            >
            <img
                v-if="isDarkMode && displayConfetti"
                src="@/assets/celebrations/darkModeConfetti.gif"
                class="quiz-result__celebration-gif"
                alt=""
            >
            <div
                class="quiz-result__stats"
                :class="{
                    'quiz-result__stats--level-up': initialLevelProgress && showLevelUpProgressMessage,
                }"
            >
                <QuizResultLevelBadge
                    v-if="initialLevelProgress && showLevelUpProgressMessage"
                    ref="quiz-result__level-up-badge"
                    class="quiz-result__level-up-badge"
                    :is-dark-mode="isDarkMode"
                    size="large"
                    :status="badgeStatus"
                    :completed-all-subjects="hasCompletedAllSubjects"
                    :level-num="initialLevelProgress.level"
                    :percent-start="(
                        initialLevelProgress.score / initialLevelProgress.goal
                    ) * 100"
                    :percent-stop="(
                        (initialLevelProgress.score + (scoreIncrease)) / initialLevelProgress.goal
                    ) * 100"
                    :animate="true"
                />
                <Gauge
                    v-else
                    :key="`${quizScore}-${communityScore}-${gaugeSize}`"
                    class="quiz-result__gauge"
                    :score="quizScore"
                    :theme="isDarkMode ? 'dark' : 'light'"
                    :community-score="isRecallChallengeQuiz ? null : communityScore"
                    :size="gaugeSize"
                    :is-recall-challenge-quiz="showRecallChallengeProgressMessage"
                />
                <div
                    v-if="showRecallChallengeProgressMessage || showLevelUpProgressMessage"
                    class="quiz-result__small-stats"
                    :class="{
                        'quiz-result__small-stats--level-up-progress-message': showLevelUpProgressMessage,
                        'quiz-result__small-stats--recall-challenge-progress-message':
                            showRecallChallengeProgressMessage,
                    }"
                >
                    <QuizResultHeaderMessage
                        :is-level-up-quiz="isLevelUpQuiz"
                        :message="quizResultsFeedback"
                    />
                </div>
                <div
                    v-else
                    class="quiz-result__small-stats"
                >
                    <Stat
                        :theme="isDarkMode ? 'dark' : 'light'"
                        class="quiz-result__correct"
                        label="Answered Correctly"
                    >
                        <template #value>
                            {{ correctCount }}
                            <span class="quiz-result__correct-slash">/</span>
                            {{ totalCount }}
                        </template>
                    </Stat>
                    <Stat
                        :theme="isDarkMode ? 'dark' : 'light'"
                        class="quiz-result__time-per-question"
                        :value="timePerQuestion"
                        label="Avg Time Per Question"
                    />
                    <Stat
                        :theme="isDarkMode ? 'dark' : 'light'"
                        class="quiz-result__quiz-time"
                        :value="quizTime"
                        :label="isMockExam ? 'Exam Time' : 'Quiz Time'"
                    />
                </div>
            </div>
            <div
                v-if="!isLoading && showTrialUpgradeBanner"
                v-dark
                class="quiz-result__trial-banner"
            >
                <div
                    class="quiz-result__trial-banner-text"
                    v-dark
                >
                    1 level down! Subscribe to progress through more difficult levels.
                </div>
                <PocketButton
                    class="quiz-result__trial-banner-btn"
                    :type="tabletOrMobileScreenSize ? 'primary' : 'tertiary'"
                    :is-dark-mode="isDarkMode"
                    @click="upgradeToPremium"
                >
                    Upgrade to Premium
                </PocketButton>
            </div>
            <div
                v-if="!showRecallChallengeProgressMessage && !showLevelUpProgressMessage"
                class="quiz-result__mobile-stats"
            >
                <div
                    v-dark
                    class="quiz-result__mobile-stat"
                    :class="{ [`quiz-result__mobile-stat--${scoreColor}`]: true }"
                >
                    <div v-dark class="quiz-result__mobile-stat-number">
                        {{ correctCount }}
                        <span class="quiz-result__correct-slash">/</span>
                        {{ totalCount }}
                    </div>
                    <div v-dark class="quiz-result__mobile-stat-label">
                        Answered Correctly
                    </div>
                </div>
                <div
                    v-dark
                    class="quiz-result__mobile-stat"
                    :class="{ [`quiz-result__mobile-stat--${scoreColor}`]: true }"
                >
                    <div v-dark class="quiz-result__mobile-stat-number">
                        {{ quizTime }}
                    </div>
                    <div v-dark class="quiz-result__mobile-stat-label">
                        {{ isMockExam ? 'Exam Time' : 'Quiz Time' }}
                    </div>
                </div>
            </div>
            <RecommendedNextCTA v-if="!isLoading && showRecommendedNextCTA" />
            <div
                v-if="!isLoading && (showQuizCTABanner || showMockExamCTABanner)"
                v-dark
                class="quiz-result__share-cta"
                :class="{
                    'quiz-result__share-cta--with-recommended-next': !isLoading && showRecommendedNextCTA
                }"
            >
                <img
                    class="quiz-result__tada"
                    src="/img/study/tada.png"
                    alt=""
                >
                <div v-dark class="quiz-result__share-cta-message">
                    {{ showQuizCTABanner ? 'You are crushing it!' : 'Congrats! ' }}
                    Share the love. Give 20% off a new subscription.
                    <PocketButton
                        class="quiz-result__share-cta-btn"
                        :class="{
                            'quiz-result__share-cta-btn--mock-exam': showMockExamCTABanner,
                        }"
                        type="tertiary"
                        :is-dark-mode="isDarkMode"
                        @click="goToSharePage"
                    >
                        Refer Friends
                    </PocketButton>
                </div>
            </div>
            <PocketButton
                v-if="
                    (isLevelUpQuiz && !showRecommendedNextCTA && hasActiveSubscription)
                        || (isLevelUpQuiz && !hasUsedLevelUpTrial)"
                :is-dark-mode="isDarkMode"
                class="quiz-result__mobile-cta"
                :class="{
                    'quiz-result__mobile-cta--show-banner': showQuizCTABanner,
                }"
                @click="goToLevelUp"
            >
                Keep Studying
            </PocketButton>
            <PocketButton
                v-if="!isLoading && showHowYouHeardAboutUsCTA"
                :is-dark-mode="isDarkMode"
                class="quiz-result__mobile-cta"
                :class="{
                    'quiz-result__mobile-cta--show-banner': showQuizCTABanner,
                }"
                @click="goToHeardAboutUsAsk"
            >
                Continue
            </PocketButton>
            <PocketButton
                v-if="
                    !isLoading
                        && canRetakeQuiz
                        && !isMockExam
                        && !showHowYouHeardAboutUsCTA
                "
                :is-dark-mode="isDarkMode"
                class="quiz-result__mobile-cta"
                :class="{
                    'quiz-result__mobile-cta--show-banner': showQuizCTABanner,
                }"
                @click="retakeQuiz"
            >
                Retake Quiz
            </PocketButton>
            <SubjectInsights
                v-if="isMockExam"
                class="quiz-result__subject-insights"
                :class="{
                    'quiz-result__subject-insights--banner': !isLoading && showMockExamCTABanner,
                }"
                :is-mock-exam="isMockExam"
                :mock-exam-subject-insights="mockExamSubjectInsights"
            />
            <div
                class="quiz-result__tabs"
                :class="{
                    'quiz-result__tabs--mock-exam': isMockExam,
                    'quiz-result__tabs--show-banner': showQuizCTABanner,
                    'quiz-result__tabs--show-level-up-progress-message': showLevelUpProgressMessage,
                    'quiz-result__tabs--show-recall-challenge-progress-message': showRecallChallengeProgressMessage,
                    'quiz-result__tabs--show-recommended-next': !isLoading && showRecommendedNextCTA,
                    'quiz-result__tabs--trial-upgrade-banner': showTrialUpgradeBanner
                }"
                role="tablist"
            >
                <ReviewTab
                    v-for="tab in tabs"
                    :key="tab"
                    :is-active="activeTab === tab"
                    :label="tab"
                    :count="questionCounts[tab]"
                    role="tab"
                    :aria-selected="activeTab === tab ? 'true' : 'false'"
                    @keydown.enter="activeTab = tab"
                    @click="activeTab = tab"
                />
            </div>
        </div>
        <div
            v-if="!currentTabQuestions.length"
            class="quiz-result__blank"
        >
            <EmptyState
                v-if="activeTab === 'All'"
                title="Let’s get studying!"
                message="Then come back here to review all of your answered questions."
                :is-dark-mode="isDarkMode"
            />
            <EmptyState
                v-else-if="activeTab === 'Flagged'"
                state="flagged"
                :title="breakpoint !== 'black-bear' ? 'You haven’t flagged any questions.' : '0 Flagged Questions'"
                :message="breakpoint !== 'black-bear' ?
                    'Click the flag icon at the bottom of any question and you can review them here.' :
                    'Tap the flag icon at the bottom of any question and you can review them here.'
                "
                backgroundColor="white"
                :is-dark-mode="isDarkMode"
            />
            <EmptyState
                v-if="activeTab === 'Incorrect'"
                state="incorrect"
                :title="breakpoint !== 'black-bear' ?
                    'You don’t have any incorrect questions.' : '0 Incorrect Questions'"
                message="Impressive!"
                backgroundColor="white"
                :is-dark-mode="isDarkMode"
            />
            <EmptyState
                v-else-if="activeTab === 'Correct'"
                state="correct"
                :title="breakpoint !== 'black-bear' ?
                    'You don’t have any correct questions.' : '0 Correct Questions'"
                backgroundColor="white"
                :is-dark-mode="isDarkMode"
            />
        </div>
        <div class="quiz-result__questions">
            <QuestionCard
                v-for="question in currentTabQuestions"
                :key="question.serial"
                class="quiz-result__question"
                :question="question"
                @click="questionClicked(question)"
                @keydown.enter="questionClicked(question)"
            />
        </div>
    </div>
</template>

<script setup lang="ts">
import { ref, computed, watch, nextTick, onMounted, onBeforeUnmount } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import QuizResultHeader from '@/components/QuizResult/QuizResultHeader.vue'
import { quizModule } from '@/store/quiz/module'
import { userModule } from '@/store/user/module'
import UIKit, { utils } from '@pocketprep/ui-kit'
import type { TActiveQuiz } from '@/store/quiz/state'
import {
    allowedQuestionsPerMinute,
    getScoreRange,
    getHoursMinutesSeconds,
    localISODateString,
    generateAnswerSeeds,
} from '@/utils'
import { globalQuestionMetricModule } from '@/store/globalQuestionMetric/module'
import { screenModule } from '@/store/screen/module'
import { questionModule } from '@/store/question/module'
import Gauge from '@/components/Gauge.vue'
import Stat from '@/components/Stat.vue'
import SubjectInsights from '@/components/SubjectInsights.vue'
import ReviewTab from '@/components/ReviewTab.vue'
import QuestionCard, { type TQuestionCardInfo } from '@/components/QuestionCard.vue'
import { userExamMetadataModule } from '@/store/userExamMetadata/module'
import { subscriptionModule } from '@/store/subscription/module'
import { examMetadataModule } from '@/store/examMetadata/module'
import { bundleModule } from '@/store/bundle/module'
import QuizResultBlob from '@/assets/quiz-result/QuizResultBlob.vue'
import type { Study } from '@pocketprep/types'
import { qotdModule } from '@/store/qotd/module'
import { analyticsModule } from '@/store/analytics/module'
import { stripHtmlTags } from '@/utils'
import RecommendedNextCTA from '@/components/RecommendedNext/RecommendedNextCTA.vue'
import QuizResultHeaderMessage from '@/components/QuizResult/QuizResultHeaderMessage.vue'
import QuizResultLevelBadge from '@/components/QuizResult/QuizResultLevelBadge.vue'
import TutorEngagementSurvey from '@/components/Tutor/TutorEngagementSurvey.vue'
import { toastModule } from '@/store/toast/module'
import { tutorModule } from '@/store/tutor/module'
import ConceptQuizSurvey from '@/components/RecommendedNext/ConceptQuizSurvey.vue'
import { upgradeModule } from '@/store/upgrade/module'

export type TTabName = 'All' | 'Flagged' | 'Incorrect' | 'Correct'
type TMockExamSubjects = {
    id: string
    name: string
    correctCount: number
    answeredCount: number
}

type TTutorEngagementFeedback = {
    helpfulness: string
    trust: string
    customResponse: string
}

const {
    Button: PocketButton,
    EmptyState,
} = UIKit

const route = useRoute()
const router = useRouter()

const isLoading = ref(true)
const tabs: TTabName[] = [
    'All',
    'Flagged',
    'Incorrect',
    'Correct',
]
const activeTab = ref<TTabName>('All')
const fromName = ref<string | null>(null)
const confettiTimerInterval = ref<ReturnType<typeof setInterval> | null>(null)
const confettiTime = ref(0)
const showConfetti = ref(true)
const showQuizCTABanner = ref(false)
const showMockExamCTABanner = ref(false)
const version = ref(import.meta.env.VUE_APP_VERSION)
const badgeStatus = ref<'active' | 'complete'>('active')
const showHowYouHeardAboutUsCTA = ref(false)
const showTutorEngagementSurvey = ref(false)
const showConceptQuizSurvey = ref(false)
const showTrialUpgradeBanner = ref(false)

const isDarkMode = computed(() => {
    return userModule.state.settings.isDarkMode
})

const breakpoint = computed(() => {
    return screenModule.getters.getBreakpoint()
})

const uem = computed(() => {
    return userExamMetadataModule.getters.getCurrentUserExamMetadata()
})

const quizId = computed(() => {
    return route.params['quizId'] as string
})

const hasSubscription = computed(() => {
    return subscriptionModule.getters.getSubscriptionForExamId()
})

const currentQuiz = computed(() => {
    return quizModule.getters.getQuizById(quizId.value)
})

const isMockExam = computed(() => {
    return currentQuiz.value?.mode === 5
})

const isLevelUpQuiz = computed(() => {
    return currentQuiz.value?.mode === 6
})

const isRecallChallengeQuiz = computed(() => {
    return currentQuiz.value?.mode === 7
})

const scenarioPartLabel = (question: Study.Class.QuestionJSON) => {
    return questionModule.getters.getQuestionScenarioPartLabel(question)
}

const canRetakeQuiz = computed(() => {
    return !isLoading.value
        && !allTabQuestions.value.All.some(q => q.isLocked)
        && currentQuiz.value
        && utils.studyModes[currentQuiz.value.mode as -1 | 0 | 2 | 3 | 4 | 10]
        && !isLevelUpQuiz.value
        && !prebuiltQuiz.value
        && !isRecallChallengeQuiz.value
        && !showRecommendedNextCTA.value
        && !isTakeABreakRec.value
})

const currentQuizQuestions = computed(() => {
    if (!currentQuiz.value) {
        return []
    }

    const answerSerials = currentQuiz.value.answers.map(ans => ans.questionSerial)
    const allQuestions = quizModule.getters.getAnsweredQuestions()

    return allQuestions.filter(q => answerSerials.includes(q.serial))
})

const currentQuizQuestionsSerialLib = computed(() => {
    if (!currentQuiz.value) {
        return {}
    }

    return currentQuizQuestions.value.reduce<{ [serial: string]: Study.Class.QuestionJSON }>((acc, q) => {
        acc[q.serial] = q

        return acc
    }, {})
})

const isHundredPercent = computed(() => {
    return correctCount.value === totalCount.value && correctCount.value >= 10
})

const retakenQuiz = computed(() => {
    return fromName.value && fromName.value === 'quiz-result'
})

const currentQuizAnswers = computed(() => {
    if (!currentQuiz.value) {
        return []
    }

    return currentQuiz.value.answers
})

const correctAnswers = computed(() => {
    return currentQuizAnswers.value.filter(ans => ans.isCorrect).map(ans => ans.questionSerial)
})

const flaggedSerials = computed(() => {
    return userExamMetadataModule.getters.getFlaggedQuestions()
})

const allTabQuestions = computed(() => {
    return currentQuizAnswers.value.reduce<Record<TTabName, TQuestionCardInfo[]>>((acc, a) => {
        const q = currentQuizQuestionsSerialLib.value[a.questionSerial]
        if (!q) {
            return acc
        }
        const subject = q.subject as Study.Class.SubjectJSON
        const subjectName = subject.name
        const questionScenarioPartLabelResult = scenarioPartLabel(q)
        const prompt = stripHtmlTags(q.prompt)

        const questionCardInfo: TQuestionCardInfo = {
            serial: q.serial,
            subjectName: subjectName,
            prompt,
            isFlagged: flaggedSerials.value.includes(q.serial),
            isCorrect: correctAnswers.value.includes(q.serial),
            isLocked: !hasSubscription.value && !q.isFree && a.questionSerial !== todaysQotD.value?.serial,
            scenarioPartLabel: questionScenarioPartLabelResult,
        }

        if (questionCardInfo.isFlagged) {
            acc.Flagged.push(questionCardInfo)
        }

        if (questionCardInfo.isCorrect) {
            acc.Correct.push(questionCardInfo)
        } else {
            acc.Incorrect.push(questionCardInfo)
        }

        acc.All.push(questionCardInfo)
        return acc
    }, { 'All': [], 'Flagged': [], 'Incorrect': [], 'Correct': [] })
})

const mockExamSubjectInsights = computed(() => {
    const mockExamSubjects: TMockExamSubjects[] = []

    allTabQuestions.value['All'].forEach(sub => {
        if (mockExamSubjects.length === 0) {
            mockExamSubjects.push({
                id: sub.subjectName,
                name: sub.subjectName,
                correctCount: sub.isCorrect ? 1 : 0,
                answeredCount: 1,
            })
        } else {
            const subjectIndex = mockExamSubjects.findIndex(s => s.id === sub.subjectName)
            if (subjectIndex < 0) {

                mockExamSubjects.push({
                    id: sub.subjectName,
                    name: sub.subjectName,
                    correctCount: sub.isCorrect ? 1 : 0,
                    answeredCount: 1,
                })

            } else {
                const mockExamSubjectObject = mockExamSubjects[subjectIndex]
                if (mockExamSubjectObject && sub.isCorrect) {
                    mockExamSubjectObject.correctCount += 1
                }

                if (mockExamSubjectObject) {
                    mockExamSubjectObject.answeredCount += 1
                }

            }
        }
    })

    return mockExamSubjects
})

const currentTabQuestions = computed(() => {
    return allTabQuestions.value[activeTab.value]
})

const questionCounts = computed(() => {
    return {
        'All': allTabQuestions.value.All.length,
        'Flagged': allTabQuestions.value.Flagged.length,
        'Incorrect': allTabQuestions.value.Incorrect.length,
        'Correct': allTabQuestions.value.Correct.length,
    } as const
})

const correctCount = computed(() => {
    return currentQuiz.value?.correctCount || 0
})

const incorrectCount = computed(() => {
    return currentQuiz.value?.incorrectCount || 0
})

const totalCount = computed(() => {
    return correctCount.value + incorrectCount.value
})

const quizScore = computed(() => {
    if (!currentQuiz.value) {
        return null
    }
    return Math.round((correctCount.value / (totalCount.value || Infinity)) * 100)
})

const scoreColor = computed(() => {
    if (quizScore.value === null) {
        return null
    }

    if (showRecallChallengeProgressMessage.value || showLevelUpProgressMessage.value) {
        return 'purple'
    }

    const range = getScoreRange(quizScore.value)
    return range === 'low' ? 'red' : range === 'medium' ? 'yellow' : 'green'
})

const timePerQuestion = computed(() => {
    const durationSeconds = currentQuiz.value?.durationSeconds || 0
    const secondsPerQuestion = Math.round(durationSeconds / (totalCount.value || Infinity))
    const minutes = Math.floor(secondsPerQuestion / 60)
    const seconds = secondsPerQuestion % 60
    return minutes ? `${minutes}m ${seconds}s` : `${seconds}s`
})

const quizTime = computed(() => {
    const { hours, minutes, seconds } = getHoursMinutesSeconds(currentQuiz.value?.durationSeconds || 0)
    return hours
        ? `${hours}h ${minutes}m`
        : minutes
            ? `${minutes}m ${seconds}s`
            : `${seconds}s`
})

const communityScore = computed(() => {
    if (isLevelUpQuiz.value) {
        return null
    } else {
        return globalQuestionMetricModule.getters.getQuizCommunityAverage(quizId.value)
    }
})

const gaugeSize = computed(() => {
    const breakpointVal = screenModule.getters.getBreakpoint()
    return breakpointVal === 'black-bear' ? 'large' : 'medium'
})

const todaysQotD = computed(() => {
    return qotdModule.getters.getQotDQuestion()
})

const hasActiveSubscription = computed(() => {
    return subscriptionModule.getters.getSubscriptionForExamId()
})

const hasUsedLevelUpTrial = computed(() => {
    return userModule.state.user?.crossPlatformConfig?.levelUpFreeTrial?.hasUsedLevelUpTrial
})

const tabletOrMobileScreenSize = computed(() => {
    return breakpoint.value === 'black-bear' || breakpoint.value === 'brown-bear'
})

const canShowQuizResultsHeaderActions = computed(() => {
    return (!showRecommendedNextCTA.value && !!hasActiveSubscription.value)
        || (isLevelUpQuiz.value && !hasUsedLevelUpTrial.value)
})

const allQuizzes = computed(() => {
    return quizModule.getters.getQuizzes()
})

const takenQuizzesNum = computed(() => {
    return allQuizzes.value ? allQuizzes.value.length : 0
})

const quizMessageConfig = computed(() => {
    return userExamMetadataModule.getters.getMessageReferralConfig()?.quizResults
})

const hasSeenCTAThisMonth = computed(() => {
    const currentMonth = new Date().getMonth()
    const quizLastSeenMonth = quizMessageConfig.value?.lastSeen?.iso
        && new Date(quizMessageConfig.value.lastSeen.iso).getMonth()
    return currentMonth === quizLastSeenMonth
})

const showQuizCTA = computed(() => {
    // showQuizCTA and showMockExamCTA are called in multiple spots
    // go ahead and return false for showQuizCTA is quizMode is ME
    if (isMockExam.value) {
        return false
    }

    // Show only after taking a quiz not after reviewing a quiz
    if (!fromName.value) {
        return false
    }

    // Show to subscribed premium users (not users studying free exams)
    if (typeof hasActiveSubscription.value !== 'object') {
        return false
    }

    // Do not show if user has seen the quiz || mock exam CTA during current month
    if (hasSeenCTAThisMonth.value) {
        return false
    }

    // Has taken 20+ quizzes
    if (takenQuizzesNum.value < 20) {
        return false
    }

    // Scored 100%
    if (!isHundredPercent.value) {
        return false
    }

    return true
})

const showMockExamCTA = computed(() => {
    // showQuizCTA and showMockExamCTA are called in multiple spots
    // go ahead and return false for showMockExamCTA is quizMode is !ME
    if (!isMockExam.value) {
        return false
    }

    // Show only after taking a quiz not after reviewing a quiz
    if (!fromName.value) {
        return false
    }

    // Show to subscribed premium users (not users studying free exams)
    if (typeof hasActiveSubscription.value !== 'object') {
        return false
    }

    // Do not show if user has seen the quiz || mock exam CTA during current month
    if (hasSeenCTAThisMonth.value) {
        return false
    }

    // Scored 80% or higher
    if ((correctCount.value / totalCount.value) <= 0.80) {
        return false
    }

    return true
})

const currentExam = computed(() => {
    return examMetadataModule.getters.getCurrentExamMetadata()
})

const currentBundle = computed(() => {
    return bundleModule.getters.getCurrentBundle()
})

const secondsBetweenRetakes = computed(() => {
    const quizIso = currentQuiz.value?.createdAt
    if (quizIso) {
        const now = new Date()
        const quizDate = new Date(quizIso)
        const differenceInSeconds = (now.getTime() - quizDate.getTime()) / 1000
        return Math.round(differenceInSeconds)
    }
    return 0
})

const prebuiltQuiz = computed(() => {
    return currentQuiz.value?.prebuiltQuiz
})

const levelUpQuizLevel = computed(() => {
    return currentQuiz.value?.levelUpProgress?.level
})

const levelUpQuizSubjectId = computed(() => {
    return currentQuiz.value?.levelUpProgress?.subjectId
})

const numOfLevelsInSubject = computed(() => {
    return examMetadataModule.getters.getLevelsInSubject(levelUpQuizSubjectId.value)?.length
})

const quizResultsFeedback = computed(() => {
    // eslint-disable-next-line no-shadow -- inner variable intentionally shadows computed name
    const quizResultsFeedback = localStorage.getItem('quizResultsFeedback')
    const feedback = JSON.parse(quizResultsFeedback || '{}')
    return feedback.progressDescription || ''
})

const showRecallChallengeProgressMessage = computed(() => {
    return isRecallChallengeQuiz.value
        && quizResultsFeedback.value
        && router.options.history.state.back !== '/stats'
})

const showLevelUpProgressMessage = computed(() => {
    return isLevelUpQuiz.value
        && quizResultsFeedback.value
        && router.options.history.state.back !== '/stats'
})

const initialLevelProgress = computed(() => {
    return currentQuiz.value?.levelUpProgress
})

const scoreIncrease = computed(() => {
    return currentQuiz.value?.correctCount || 0
})

const didLevelUp = computed(() => {
    if (!initialLevelProgress.value) {
        return false
    }
    return Boolean((
        initialLevelProgress.value.score + (scoreIncrease.value)
    ) >= initialLevelProgress.value.goal)
})

const levelUpProgress = computed(() => {
    return userExamMetadataModule.getters.getLevelUpProgress()
})

const subjectsWithLevels = computed(() => {
    return examMetadataModule.state.subjectsWithLevels
})

const hasCompletedAllSubjects = computed(() => {
    const incompleteSubjects = levelUpProgress.value.filter(progress => {
        const subjectNumLevels = subjectsWithLevels.value
            .find(subjectWithLevels => subjectWithLevels.subjectId === progress.subjectId)?.levels?.length
        return subjectNumLevels && progress.level <= subjectNumLevels && progress.score < progress.goal
    })

    return incompleteSubjects.length < 1
})

const displayConfetti = computed(() => {
    return !isLoading.value
        && isHundredPercent.value
        && showConfetti.value
        && !retakenQuiz.value
        && !showLevelUpProgressMessage.value
        && !showRecallChallengeProgressMessage.value
})

const isTakeABreakRec = computed(() => {
    return recommendedNext.value?.recommenderClass === 'TakeABreak'
})

const showRecommendedNextCTA = computed(() => {
    // no recommendations for Mock Exam at this time
    if (isMockExam.value) {
        return false
    }

    if (showHowYouHeardAboutUsCTA.value) {
        return false
    }

    if (isTakeABreakRec.value) {
        return false
    }

    return quizModule.state.recommendedNext?.quizId === currentQuiz.value?.objectId
})

const currentOrganizationId = computed(() => {
    return userModule.getters.getCurrentOrganizationId()
})

const userDomain = computed(() => {
    return userModule.getters.getUserDomain()
})

const percentCorrect = computed(() => {
    return correctCount.value / currentQuizAnswers.value.length
})

const hasSeenATutorSurvey = computed(() => {
    const hasSeenTutorEngagementSurvey = userModule.state.user?.webConfig?.hasSeenTutorEngagementSurvey
    const hasSeenTutorNonEngagementSurvey = userModule.state.user?.webConfig?.hasSeenTutorNonEngagementSurvey
    return hasSeenTutorEngagementSurvey || hasSeenTutorNonEngagementSurvey
})

const hasSentATutorMessageDuringQuiz = computed(() => {
    return tutorModule.state.hasSentTutorMessageDuringQuiz
})

const canShowTutorEngagementSurvey = computed(() => {
    return !hasSeenATutorSurvey.value && hasSentATutorMessageDuringQuiz.value
})

const conceptQuizzesTakenCount = computed(() => {
    return quizModule.getters.getQuizzes().filter(q => q.mode === 12).length
})

const allConceptQuizzes = computed(() => {
    return quizModule.getters.getConceptQuizzes()
})

const conceptQuizForCurrentQuiz = computed(() => {
    const currentQuizConceptQuizId = currentQuiz.value?.conceptQuiz?.objectId

    return allConceptQuizzes.value.find(cq => cq.objectId === currentQuizConceptQuizId)
})

const conceptQuizConcept = computed(() => {
    return conceptQuizForCurrentQuiz.value?.concept
})

const recommendedNext = computed(() => {
    return quizModule.state.recommendedNext
})

onMounted(async () => {
    await Promise.all([
        userModule.actions.fetchUserData(),
        userExamMetadataModule.actions.fetchUserExamMetadata(),
        questionModule.actions.fetchSerialQuestionInfoLib(),
        quizModule.actions.fetchAnsweredQuestions(),
        globalQuestionMetricModule.actions.fetchGlobalQuestionMetrics(),
        qotdModule.actions.fetchCurrentQotDQuestion(),
        bundleModule.actions.fetchBundles(),
        examMetadataModule.actions.fetchExamMetadata(),
    ])

    // if for some reason we don't have all the questions for the specific quiz, fetch them
    if (allTabQuestions.value['All'].length !== currentQuizAnswers.value.length) {
        await quizModule.actions.fetchAnsweredQuestions(quizId.value)
    }

    window.addEventListener('keydown', keydownListener)

    if (typeof route.query.type === 'string') {
        activeTab.value = route.query.type as TTabName
    }
    if (typeof route.query.from === 'string') {
        fromName.value = route.query.from
    }

    if (Object.keys(route.query).length && quizId.value) {
        await router.replace({
            name: 'quiz-result',
            params: {
                quizId: quizId.value,
            },
        })
    }

    // if for some reason the quiz you're trying to view doesn't exist, redirect back to study
    if (!currentQuiz.value) {
        if (route.name !== 'study') {
            await router.push({
                name: 'study',
            })
        }
    }

    showQuizCTABanner.value = showQuizCTA.value
    showMockExamCTABanner.value = showMockExamCTA.value

    if (canShowTutorEngagementSurvey.value) {
        showTutorEngagementSurvey.value = true
    } else if (fromName.value && conceptQuizForCurrentQuiz.value && conceptQuizzesTakenCount.value === 3) {
        showConceptQuizSurvey.value = true
    }

    /* Start Heard About Us Ask Experiment */
    // showHowYouHeardAboutUsCTA if it is the users first Quick 10, Timed, or BYOQ
    // If they have not seen the ask before
    // If they are coming from a quiz or back from the HeardAboutUsAsk component
    // And if the how-you-heard-about-us feature flag is on
    const isEligibleQuizMode = currentQuiz.value?.mode !== undefined && [ 0, 4, 10 ].includes(currentQuiz.value.mode)
    const hasSeenHeardAboutUsAsk = userModule.state.user?.webConfig?.hasSeenHeardAboutUsAsk
    const eligibleFromRoute = router.options.history.state.back === '/quiz'
        || router.options.history.state.back === `/heard-about-us-ask/${currentQuiz.value?.objectId}`

    if (
        isEligibleQuizMode
        && !hasSeenHeardAboutUsAsk
        && eligibleFromRoute
    ) {
        const howYouHeardAboutUsFlag = await analyticsModule.actions.amplitudeFetchVariant('how-you-heard-about-us')
        if (howYouHeardAboutUsFlag?.value === 'on') {
            const recommendedConfig = recommendedNext.value?.config

            showHowYouHeardAboutUsCTA.value =
                recommendedConfig?.quizMode === 0 && !showTutorEngagementSurvey.value
        }
    }
    /* End Heard About Us Ask Experiment */

    if (
        hasUsedLevelUpTrial.value
        && !hasActiveSubscription.value
        && isLevelUpQuiz.value
        && didLevelUp.value
        && router.options.history.state.back !== '/stats'
    ) {
        showTrialUpgradeBanner.value = true
    }

    isLoading.value = false

    if (!showTutorEngagementSurvey.value) {
        nextTick(() => {
            const titleEl: HTMLElement | null = document.querySelector('.quiz-result-header__mode-name')
            titleEl?.focus()
        })
    }

    confettiTimerInterval.value = setInterval(() => {
        confettiTime.value = confettiTime.value + 1
        if (confettiTimerInterval.value && confettiTime.value === 2) {
            showConfetti.value = false
            clearInterval(confettiTimerInterval.value)
        }
    }, 1000)

    if (didLevelUp.value) {
        setTimeout(() => {
            badgeStatus.value = 'complete'
        }, 1500)
    }
})

onBeforeUnmount(() => {
    window.removeEventListener('keydown', keydownListener)
    if (confettiTimerInterval.value) {
        clearInterval(confettiTimerInterval.value)
    }
})

const keydownListener = (e: KeyboardEvent) => {
    if (e.code === 'Escape') {
        e.stopPropagation()
        closeResults()
    }
}

const retakeQuiz = () => {
    if (!currentQuiz.value) {
        throw new Error('QuizResult.retakeQuiz: No currentQuiz found')
    }

    const code = String(currentQuiz.value.mode) as keyof typeof utils.studyModes
    const mode = utils.studyModes[code].shortName
    const quizQuestions = [ ...currentQuizQuestions.value ]
    const answerSeeds = generateAnswerSeeds(quizQuestions)

    const quizToRetake: TActiveQuiz = {
        questions: quizQuestions,
        answers: [],
        examGuid: currentQuiz.value.examGuid,
        mode,
        startedAt: localISODateString(),
        currentQuestionIndex: 0,
        durationSeconds: 0,
        breakDurationSeconds: 0,
        timerType: 'exam',
        answerSeeds,
    }

    // If retaking a timed quiz, give enough minutes to answer the questions according to allowedQuestionsPerMinute
    if (mode === 'timed') {
        quizToRetake.timeLimit = Math.ceil(quizQuestions.length / allowedQuestionsPerMinute)
    }

    quizModule.actions.updateActiveQuiz(quizToRetake)

    analyticsModule.actions.amplitudeTrack('Quiz Retaken', {
        bundleId: currentBundle.value?.objectId || '',
        client: 'Web Study',
        clientAppId: 'study.pocketprep.com',
        clientVersion: version.value,
        examGuid: currentExam.value?.examGuid || '',
        organizationId: currentOrganizationId.value || undefined,
        userDomain: userDomain.value || undefined,
        retakeScreen: 'Quiz Results',
        retakeSeconds: secondsBetweenRetakes.value,
    })

    router.push({ name: 'quiz' })
}

const closeResults = async () => {
    if (quizResultsFeedback.value) {
        localStorage.removeItem('quizResultsFeedback')
    }

    if (localStorage.getItem('tryLevelUpExperiment')) {
        localStorage.removeItem('tryLevelUpExperiment')
    }

    if (showHowYouHeardAboutUsCTA.value) {
        await userModule.actions.updateWebConfig({
            hasSeenHeardAboutUsAsk: true,
        })
    }

    if (fromName.value && [
        'quiz-result', 'byoq', 'mock-exam-intro', 'level-up-intro', 'assignment-intro',
    ].includes(fromName.value)) {
        router.push({ name: 'study' })
    } else if (fromName.value) {
        router.push({ name: fromName.value })
    } else {
        router.push({ name: 'stats' })
    }
}

const questionClicked = (question: TQuestionCardInfo) => {
    router.push({
        name: 'question-review',
        query: {
            ...route.query,
            questionSerial: question.serial,
            quizId: quizId.value,
            type: activeTab.value,
        },
    })
}

const goToLevelUp = () => {
    router.push({
        name: 'level-up-intro',
        query: {
            step: 'selectSubject',
        },
    })
}

const goToHeardAboutUsAsk = () => {
    router.push({
        name: 'heard-about-us-ask',
        params: {
            quizId: quizId.value,
        },
    })
}

const updateQuizMessageConfig = async () => {
    if (!uem.value) {
        return
    }
    await userExamMetadataModule.actions.upsertUserExamMetadata({
        examGuid: uem.value.examGuid,
        messageConfig: {
            ...(uem.value.messageConfig || {}),
            referral: {
                ...(uem.value.messageConfig?.referral as Study.Class.MessageConfigOptions || {}),
                quizResults: {
                    lastSeen: new Date(),
                },
            },
        },
    })
}

const goToSharePage = () => {
    router.push({
        name: 'share',
        query: {
            utm_content: 'quiz_results',
        },
    })
}

const showCTABannerChange = () => {
    if (!isLoading.value && (showQuizCTABanner.value || showMockExamCTABanner.value)) {
        updateQuizMessageConfig()
    }
}

const tutorSurveyQuizAmplitudeProps = () => {
    return {
        examMetadataId: currentExam.value?.objectId || undefined,
        examName: currentExam.value?.nativeAppName,
        quizAnswerCount: currentQuizAnswers.value.length,
        quizCorrectCount: correctCount.value,
        quizDurationSeconds: currentQuiz.value?.durationSeconds,
        quizIncorrectCount: incorrectCount.value,
        quizMode: currentQuiz.value?.mode,
        quizPercentCorrect: percentCorrect.value,
        quizStartedAt: currentQuiz.value?.startedAt.iso,
    }
}

const submitTutorEngagementSurvey = (response: TTutorEngagementFeedback) => {
    const amplitudeQuizProps = tutorSurveyQuizAmplitudeProps()
    analyticsModule.actions.amplitudeTrack('Tutor V1 Post Engagement Survey Response', {
        ...amplitudeQuizProps,
        tutorHelpfulness: response.helpfulness,
        tutorTrust: response.trust,
        tutorPostEngagementFreeText: response.customResponse,
    })

    showTutorEngagementSurvey.value = false

    toastModule.actions.displayToast({
        title: 'Thank you for your feedback!',
    })
}

const upgradeToPremium = () => {
    upgradeModule.actions.openUpgradeModal()
    analyticsModule.actions.amplitudeTrack('Level Up Trial Upgrade CTA Clicked')
}

watch([ showQuizCTABanner, showMockExamCTABanner ], () => {
    showCTABannerChange()
})
</script>

<style lang="scss" scoped>
.quiz-result {
    position: relative;
    background-color: $gray-background;
    height: 100%;
    overflow: auto;
    overflow-x: hidden;

    &__celebration-gif {
        position: absolute;
        transform: translateX(-60%);
        top: -30px;
        z-index: -1;
        width: 480px;
        height: 350px;

        @include breakpoint(brown-bear) {
            margin-left: 30px;
            top: -50px;
            transform: translateY(-10%);
            transform: translateX(-0%);
        }

        @include breakpoint(black-bear) {
            top: -60px;
            width: 510px;
            height: 435px;
            margin: auto;
        }

        @media (prefers-reduced-motion: reduce) {
            display: none
        }
    }

    &--dark {
        background-color: $charcoal;
    }

    &__blob {
        position: absolute;
        top: 0;
        left: 0;
        z-index: 2;

        @include breakpoint(black-bear) {
            display: none;
        }
    }

    &__banner {
        display: flex;
        align-items: center;
        flex-direction: column;
        position: relative;
        width: 100%;
        padding-top: 129px;
        background-color: $white;
        z-index: 1;

        &--dark {
            background-color: $brand-black;
        }

        @include breakpoint(brown-bear) {
            padding-top: 105px;
        }

        &--show-level-up-progress-message {
            padding-top: 70px;

            @include breakpoint(brown-bear) {
                padding-top: 24px;
            }
        }
    }

    &__stats {
        display: flex;
        justify-content: center;
        flex-wrap: wrap;
        max-width: 780px;

        @include breakpoint(brown-bear) {
            max-width: 500px;
        }

        &--level-up {
            position: relative;
            left: -40px;

            @include breakpoint(brown-bear) {
                flex-direction: column;
                align-items: center;
                left: 0;
            }
        }
    }

    &__small-stats {
        display: flex;

        @include breakpoint(brown-bear) {
            margin-top: 105px;
        }

        &--level-up-progress-message {
            @include breakpoint(brown-bear) {
                margin-top: 0;
            }
        }

        &--recall-challenge-progress-message {
            @include breakpoint(brown-bear) {
                margin-top: 56px;
            }

            @include breakpoint(black-bear) {
                margin-top: 105px;
            }
        }
    }

    &__trial-banner {
        display: flex;
        align-items: center;
        justify-content: space-between;
        background-color: $barely-purple;
        border: 2px solid $orchid;
        border-radius: 6px;
        padding: 16px 24px 16px 16px;
        margin-top: 44px;
        width: 780px;
        box-sizing: border-box;

        &--dark {
            border-color: $orchid;
            background-color: rgba($orchid, 0.20);
            color: $white;
        }

        @include breakpoint(brown-bear) {
            flex-direction: column;
            width: 335px;
            height: 139px;
            margin-top: 26px;
        }

        @include breakpoint(black-bear) {
            margin-top: -26px;
        }
    }

    &__trial-banner-text {
        font-size: 16px;
        line-height: 22px;
        color: $brand-black;

        &--dark {
            color: $white;
        }

        @include breakpoint(brown-bear) {
            text-align: center;
        }
    }

    &__mobile-stats {
        display: none;
        justify-content: space-between;
        width: 336px;
        color: $white;
        margin-bottom: 26px;
        margin-top: 34px;

        @include breakpoint(black-bear) {
            display: flex;
        }
    }

    &__mobile-stat {
        text-align: center;
        width: 161px;
        height: 72px;
        display: flex;
        align-items: center;
        justify-content: center;
        border-radius: 6px;
        flex-direction: column;

        &--red {
            background: $red-hint;

            &--dark {
                background: rgba($brand-red, 0.3);
            }
        }

        &--yellow {
            background: $cream-puff;

            &--dark {
                background: rgba($butterscotch, 0.18);
            }
        }

        &--green {
            background: $barely-green;

            &--dark {
                background: rgba($jungle-green, 0.2);
            }
        }
    }

    &__mobile-stat-number {
        font-size: 26px;
        letter-spacing: 0.26px;
        margin-bottom: -2px;
        font-weight: 600;
        line-height: 30px;
        color: $brand-black;

        &--dark {
            color: $white;
        }
    }

    &__mobile-stat-label {
        font-size: 14px;
        line-height: 19px;
        color: $ash;

        &--dark {
            color: $white;
        }
    }

    &__mobile-cta {
        width: 176px;
        display: none;

        @include breakpoint(black-bear) {
            display: block;

            &--show-banner {
                margin-top: 24px;
            }
        }
    }

    &__level-up-badge {
        margin-right: 66px;

        @include breakpoint(brown-bear) {
            margin-right: 0;
        }
    }

    &__gauge {
        position: relative;
        top: 12px;
        margin-right: 58px;

        @include breakpoint(brown-bear) {
            margin-right: 0;
        }
    }

    &__correct {
        width: 130px;
        margin-right: 48px;

        @include breakpoint(black-bear) {
            display: none;
        }
    }

    &__correct-slash {
        color: $pewter;
        margin: 0 2px;
    }

    &__time-per-question {
        width: 140px;
        margin-right: 38px;

        @include breakpoint(black-bear) {
            display: none;
        }
    }

    &__quiz-time {
        width: 140px;

        @include breakpoint(black-bear) {
            display: none;
        }
    }

    &__share-cta {
        display: flex;
        border-radius: 6px;
        margin-top: 80px;
        width: 780px;
        background-color: $manilla;
        border: 1px solid $banana-bread;

        &--with-recommended-next {
            margin-top: 24px;
        }

        &--dark {
            border: 1px solid rgba($cheddar, 0.50);
            background: rgba($cheddar, 0.20);
        }

        @include breakpoint(brown-bear) {
            margin-top: 40px;
            max-width: 708px;
        }

        @include breakpoint(black-bear) {
            max-width: 343px;
            height: 65px;
            margin-top: 6px;

            &--with-recommended-next {
                margin-top: 26px;
            }
        }
    }

    &__tada {
        height: 22px;
        max-width: 20px;
        margin: 12px 0 0 19px;
    }

    &__share-cta-message {
        font-size: 15px;
        font-weight: 600;
        line-height: 20px;
        text-align: left;
        display: flex;
        align-items: center;
        padding: 12px 20px 12px 10px;

        &--dark {
            color: $white;
        }

        @include breakpoint(black-bear) {
            display: block;
            font-weight: 500;
            margin-left: 6px;
            line-height: 22px;
            padding: 0;
            margin-top: 12px;
        }
    }

    &__share-cta-btn {
        margin-left: 145px;

        &--mock-exam {
            margin-left: 210px;
        }

        @include breakpoint(brown-bear) {
            margin-left: 75px;

            &--mock-exam {
                margin-left: 140px;
            }
        }

        @include breakpoint(black-bear) {
            margin-left: 0;
        }
    }

    &__subject-insights {
        margin-top: 74px;
        margin-bottom: 49px;
        width: 812px;

        &--banner {
            margin-top: 40px;
        }

        @include breakpoint(brown-bear) {
            margin-top: 60px;
            margin-bottom: 68px;
            width: 725px;

            &--banner {
                margin-top: 40px;
            }
        }

        @include breakpoint(black-bear) {
            margin-top: 46px;
            margin-bottom: 44px;
            width: 370px;
        }
    }

    &__tabs {
        display: flex;
        width: 780px;
        margin-top: 120px;

        @include breakpoint(brown-bear) {
            width: 708px;
        }

        &--mock-exam {
            margin: auto;
        }

        &--show-banner {
            margin-top: 44px;
        }

        &--show-level-up-progress-message {
            margin-top: 80px;
        }

        &--show-recall-challenge-progress-message {
            @include breakpoint(brown-bear) {
                margin-top: 80px;
            }
        }

        &--show-recommended-next {
            margin-top: 44px;
        }

        &--trial-upgrade-banner {
            margin-top: 52px;
        }

        @include breakpoint(black-bear) {
            margin-top: 52px;
            width: 336px;
        }
    }

    &__blank {
        text-align: center;
        padding-top: 56px;

        :deep(.uikit-empty-state) {
            margin: auto;
        }
    }

    &__questions {
        padding-top: 34px;
        padding-bottom: 55px;
        margin: 0 auto;
        width: 780px;

        @include breakpoint(brown-bear) {
            width: 708px;
        }

        @include breakpoint(black-bear) {
            width: 336px;
        }
    }

    :deep(.info-message--dark) {
        background-color: #494630;
    }
}
</style>
